import React from 'react';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
} from '@mui/material';

export interface IronDomeSummaryItem {
  campaignName?: string;
  offerName?: string;
  blockAnon: string;
  blockDataCenter: string;
  blockExt?: string;
  blockBadIps: string;
  blockLocations: {
    type: string;
    country:
      | {
          name: string;
          iso2: string;
        }
      | 'all';
    state:
      | {
          name: string;
          state_code: string;
        }
      | 'all';
    city:
      | {
          name: string;
          city_code: string;
        }
      | 'all';
  }[];
  ipThrottling: {
    ipThrottling: boolean;
    window: number;
    maxRequests: number;
    blacklistFor: number;
  };
  passback: string;
  passbackUrlMode: string;
  globalFallback: string;
}

interface IronDomeSummaryTableProps {
  summaryData: IronDomeSummaryItem[];
}

function IronDomeSummaryTable({ summaryData }: IronDomeSummaryTableProps) {
  if (!summaryData || summaryData.length === 0) {
    return null;
  }

  const formatLocation = (
    location: IronDomeSummaryItem['blockLocations'][0]
  ) => {
    const { type, country, state, city } = location;

    const typeLabel = type.charAt(0).toUpperCase() + type.slice(1);

    const noCountry = !country;
    const noState = !state;
    const noCity = !city;

    const allCountry = country === 'all';
    const allState = state === 'all';
    const allCity = city === 'all';

    if (noCountry) return '';
    if (allCountry) return `${typeLabel}: All countries`;

    const countryName = country.name;

    if (noState) return `${typeLabel}: ${countryName} - Invalid state`;
    if (allState) return `${typeLabel}: ${countryName} - All states`;

    const stateName = state.name;

    if (noCity)
      return `${typeLabel}: ${countryName} - ${stateName} - Invalid city`;
    if (allCity)
      return `${typeLabel}: ${countryName} - ${stateName} - All cities`;

    const cityName = city.name;

    return `${typeLabel}: ${countryName} - ${stateName} - ${cityName}`;
  };

  const renderLocationBlock = (rule: IronDomeSummaryItem) => {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 0.5 }}>
        {rule.blockLocations.map((location, locIndex) => {
          const label = formatLocation(location);

          if (!label) return null;

          return <Chip key={locIndex} label={label} sx={{ flexGrow: 0 }} />;
        })}
      </Box>
    );
  };

  const renderIpThrottlingBlock = (rule: IronDomeSummaryItem) => {
    const { ipThrottling, window, maxRequests, blacklistFor } =
      rule.ipThrottling;

    if (!ipThrottling) return <Chip label="Disabled" />;

    const label = `${window}m Window - ${maxRequests} Requests - ${blacklistFor}m Block`;

    return <Chip label={label} />;
  };

  return (
    <TableContainer component={Paper} sx={{ mt: 2, maxHeight: 600 }}>
      <Table stickyHeader size="small">
        <TableHead>
          <TableRow>
            <TableCell sx={{ fontWeight: 'bold' }}>Block Anon</TableCell>
            <TableCell sx={{ fontWeight: 'bold' }}>Block Data Center</TableCell>
            {summaryData.some((item) => item.blockExt !== undefined) && (
              <TableCell sx={{ fontWeight: 'bold' }}>Block Ext</TableCell>
            )}
            <TableCell sx={{ fontWeight: 'bold' }}>Block Bad IPs</TableCell>
            <TableCell sx={{ fontWeight: 'bold' }}>Block Locations</TableCell>
            <TableCell sx={{ fontWeight: 'bold' }}>IP Throttling</TableCell>
            <TableCell sx={{ fontWeight: 'bold' }}>Passback</TableCell>
            <TableCell sx={{ fontWeight: 'bold' }}>Passback URL Mode</TableCell>
            <TableCell sx={{ fontWeight: 'bold' }}>Global Fallback</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {summaryData.map((item, index) => (
            <TableRow key={index}>

              <TableCell>{item.blockAnon}</TableCell>
              <TableCell>{item.blockDataCenter}</TableCell>
              {summaryData.some((i) => i.blockExt !== undefined) && (
                <TableCell>{item.blockExt || 'N/A'}</TableCell>
              )}
              <TableCell>{item.blockBadIps}</TableCell>
              <TableCell>{renderLocationBlock(item)}</TableCell>
              <TableCell>{renderIpThrottlingBlock(item)}</TableCell>
              <TableCell>{item.passback}</TableCell>
              <TableCell>{item.passbackUrlMode}</TableCell>
              <TableCell sx={{ maxWidth: 200, wordBreak: 'break-all' }}>
                {item.globalFallback}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
}

export default IronDomeSummaryTable;
